import React, { useEffect, useState } from 'react'
//@ts-ignore
import { Button } from 'dsm-react-lib'
import { IconX } from '@tabler/icons-react'
import {
  getLastAgentSnapshot,
  subscribeAgentSnapshot,
  type WorkspaceContextSnapshot
} from './frontend-tools/fdc3Bridge'
import { getUserChannel } from '../Work-space/fdc3/runtime/userChannels'

/**
 * Ambient context chip (Spec 010 §8.2).
 *
 * The visible face of `workspaceContext.latestContext`: a small, dismissible chip
 * above the chat input showing what Ali currently knows and which app it came
 * from (provenance), so "tell me about this" resolves deterministically. Fed
 * entirely by the Agent connector via the bridge snapshot pub/sub — no new
 * transport. Renders nothing when Ali is off the bus or has no context.
 */
export interface AliContextBar {
  /** True when context is present and the bar has not been dismissed. */
  visible: boolean
  /** The rendered bar, or null when not visible. */
  node: React.ReactNode
}

/**
 * Shared hook backing the context bar. Returning both `visible` and `node` from a
 * single subscription lets the chat input flatten its top edge in lock-step with
 * the bar — no second subscription, and no desync with the dismiss state.
 */
export function useAliContextBar(): AliContextBar {
  const [snapshot, setSnapshot] = useState<WorkspaceContextSnapshot | null>(getLastAgentSnapshot())
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)

  useEffect(() => subscribeAgentSnapshot(setSnapshot), [])

  const context = snapshot?.latestContext
  if (!context) return { visible: false, node: null }

  const label = context.name || context.id?.ticker || context.type
  const ticker = context.id?.ticker
  const source = context._sourceAppId
  const key = `${context.type}:${label}:${ticker ?? ''}`

  if (dismissedKey === key) return { visible: false, node: null }

  const channel = snapshot?.currentChannel ? getUserChannel(snapshot.currentChannel) : undefined
  const dotColor = channel?.displayMetadata?.color ?? 'var(--accent,#6b4eff)'
  const channelName = channel?.displayMetadata?.name

  const node = (
    <div
      className="flex items-center gap-2 w-full text-xs rounded-t-[1rem] border border-b-0 theme-frame-divider bg-[var(--soft,#f4f6f8)] p-1 -mb-px"
      data-testid="ali-context-chip"
      title={`Ali context: ${label}${source ? ` (from ${source})` : ''}`}>
      <span
        aria-hidden
        className="inline-block w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: dotColor }}
      />
      <span className="font-medium truncate">
        {ticker && ticker !== label ? `${ticker} · ${label}` : label}
      </span>
      {source && <span className="opacity-60 truncate">· from {source}</span>}
      <Button
        variant="flat"
        iconOnly
        icon={<IconX size={12} />}
        aria-label="Dismiss context"
        className="ml-auto shrink-0 opacity-60 hover:opacity-100 !p-0.5"
        onClick={() => setDismissedKey(key)}
      />
    </div>
  )

  return { visible: true, node }
}

const ContextChip: React.FC = () => <>{useAliContextBar().node}</>

export default ContextChip
